"use client";

import { useEffect, useRef } from "react";

/**
 * One-shot scroll reveal — CSS-driven replacement for the per-section
 * gsap + ScrollTrigger timelines that used to run on the homepage.
 *
 * The section is "armed" (children get their hidden starting state) only
 * when it is still below the viewport at hydration, so crawlers, no-JS
 * visitors and anything already on screen always see content. Entering the
 * viewport adds `is-revealed`; homepage.css transitions the children.
 */
export function useSectionReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Already (partly) on screen — reveal without animating.
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) return;

    el.classList.add("iv-reveal-armed");
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("is-revealed");
          obs.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return ref;
}
